import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PLATFORMS, byPath } from "@/lib/platforms";
import { toast } from "sonner";
import { Menu, X } from "lucide-react";
import CrisisLine from "@/components/site/CrisisLine";

const TABS = [
  { to: "/", label: "Home" },
  { to: "/blog", label: "Blog" },
  { to: "/why", label: "Why this exists" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const { user, logout, setEntryOpen } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const current = byPath(location.pathname);
  const dark = current?.dark || current?.navDark;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className={`fixed top-0 right-0 left-0 md:left-20 z-50 nav-blur ${dark ? "nav-dark text-white" : ""} ${current?.key === "health" ? "nav-clinic" : ""}`} data-testid="main-nav">
      <div className="px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 -ml-1" data-testid="nav-mobile-menu-btn" aria-label="Platforms">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" className="md:hidden font-display font-semibold text-lg tracking-tight" data-testid="nav-wordmark">Novcovek</Link>
          <nav className="hidden md:flex items-center gap-1">
            {TABS.map((t) => {
              const active = location.pathname === t.to;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  className={`tab-pill rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap ${active ? (dark ? "bg-white/15" : "bg-white/85 shadow-sm") : dark ? "text-white/80" : "text-[var(--ink-soft)]"}`}
                  data-testid={`nav-tab-${t.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  {t.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <CrisisLine dark={dark} />
          {current && (
            <span className="hidden md:flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1.5" style={{ background: dark ? "rgba(255,255,255,0.12)" : current.soft, color: dark ? "#C7CBFA" : current.deep }} data-testid="nav-platform-indicator">
              <span className="w-2 h-2 rounded-full" style={{ background: current.hue }} />
              {current.name}
            </span>
          )}
          {user ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/record")}
                className={`flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 ${dark ? "bg-white/10" : "glass"}`}
                data-testid="nav-alias-chip"
              >
                <span className="w-6 h-6 rounded-full blob-c" style={{ background: user.colour }} />
                <span className="font-semibold text-sm">{user.alias}</span>
              </button>
              <button
                onClick={async () => { await logout(); toast("Your alias signed out. It is waiting when you return."); }}
                className="text-xs opacity-60 hover:opacity-100 transition-opacity"
                data-testid="nav-logout-btn"
              >
                Leave
              </button>
            </div>
          ) : (
            <button onClick={() => setEntryOpen(true)} className="btn-ink rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap" data-testid="nav-alias-entry-btn">
              Pick your alias
            </button>
          )}
        </div>
      </div>
      {menuOpen && (
        <div className="md:hidden border-t border-white/60 glass-strong max-h-[70vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="p-4 space-y-1">
            {TABS.map((t) => (
              <Link key={t.to} to={t.to} onClick={() => setMenuOpen(false)} className="block rounded-xl px-4 py-2.5 text-sm font-semibold">
                {t.label}
              </Link>
            ))}
            <p className="kicker text-[var(--ink-soft)] px-4 pt-3 pb-1">Platforms</p>
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.key} to={p.path} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 rounded-xl px-4 py-2.5" data-testid={`nav-mobile-platform-${p.key}`}>
                  <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: p.soft, color: p.deep }}>
                    <Icon size={15} />
                  </span>
                  <span className="text-sm font-semibold">{p.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
